// GIG Service for SPACE, PROJECT, PLAN management

class GigService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.baseUrl = this.isDevelopment ? '' : (process.env.REACT_APP_API_URL || 'https://api.astrantis.net');
  }

  // 인증 토큰 가져오기
  getAuthToken() {
    return localStorage.getItem('auth_token');
  }

  // 인증 헤더 포함 fetch (토큰 없으면 즉시 에러 — 로그인 필수 API용)
  async authenticatedFetch(url, options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }
    return this.optionalAuthFetch(url, options);
  }

  // 토큰이 있으면 Authorization: Bearer 로 첨부하고, 없어도 요청은 그대로 보낸다 (공개 열람 API용).
  // 로컬(동일 오리진)은 HttpOnly 쿠키로도 인증되지만, 배포(Pages↔API 교차 오리진)에서는
  // SameSite=Strict 쿠키가 전달되지 않으므로 Bearer가 필수다. (authService H-2 주석과 동일)
  async optionalAuthFetch(url, options = {}) {
    const token = this.getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token && !headers.Authorization && !headers.authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 401) throw new Error('인증이 필요합니다.');
      if (response.status === 403) throw new Error('권한이 없습니다.');
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  // ========================================
  // SPACE API
  // ========================================

  /**
   * SPACE 목록 조회
   * 'popular'|'all'은 백엔드가 비로그인 사용자에게도 공개하는 목록이므로 optionalAuthFetch를 사용한다.
   * 'mine'은 토큰이 없으면 서버가 401을 내려주고(자체적으로 인증을 강제), 호출부는 이미 이를 catch 처리한다.
   * @param {string} type - 'mine' | 'popular' | 'all'
   * @param {number} page
   * @param {number} pageSize
   */
  async getSpaces(type = 'all', page = 1, pageSize = 10) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces?type=${type}&page=${page}&pageSize=${pageSize}`;
      return await this.optionalAuthFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 상세 조회 — 비로그인/비멤버도 호출 가능 (공개 필드만 반환, viewer 컨텍스트 포함)
   * @param {number} spaceId
   * @returns {Promise<{ success: boolean, space: object, viewer: { role: string, is_member: boolean, is_admin: boolean, application_status: string|null } }>}
   */
  async getSpace(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}`;
      return await this.optionalAuthFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 수정
   * @param {number} spaceId
   * @param {Object} data - { title?, description?, startDate?, endDate?, thumbnail? }
   */
  async updateSpace(spaceId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}`;
      return await this.authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 삭제
   * @param {number} spaceId
   */
  async deleteSpace(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}`;
      return await this.authenticatedFetch(url, {
        method: 'DELETE'
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * 새 SPACE 생성
   * @param {Object} data - { title, description, startDate?, endDate?, thumbnail? }
   */
  async createSpace(data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // PROJECT API
  // ========================================

  /**
   * PROJECT 목록 조회
   * @param {number} spaceId
   */
  async getProjects(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * PROJECT 상세 조회
   * @param {number} spaceId
   * @param {number} projectId
   */
  async getProject(spaceId, projectId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * 새 PROJECT 생성
   * @param {number} spaceId
   * @param {Object} data - { title, description?, icon?, startDate?, endDate? }
   */
  async createProject(spaceId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * PROJECT 수정
   * @param {number} spaceId
   * @param {number} projectId
   * @param {Object} data - { title?, description?, icon?, startDate?, endDate? }
   */
  async updateProject(spaceId, projectId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}`;
      return await this.authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // PLAN API
  // ========================================

  /**
   * PLAN 목록 조회 (주차별 그룹핑)
   * @param {number} spaceId
   * @param {number} projectId
   */
  async getPlans(spaceId, projectId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * PLAN 상세 조회
   * @param {number} spaceId
   * @param {number} projectId
   * @param {number} planId
   */
  async getPlan(spaceId, projectId, planId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * 새 PLAN 생성
   * @param {number} spaceId
   * @param {number} projectId
   * @param {Object} data - { title, content?, startDate?, endDate?, status?, weekNumber? }
   */
  async createPlan(spaceId, projectId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * PLAN 수정
   * @param {number} spaceId
   * @param {number} projectId
   * @param {number} planId
   * @param {Object} data - { title?, content?, startDate?, endDate?, status? }
   */
  async updatePlan(spaceId, projectId, planId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}`;
const result = await this.authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify(data)
      });
      

      return result;
    } catch (error) {

      throw error;
    }
  }

  /**
   * PLAN 삭제
   * @param {number} spaceId
   * @param {number} projectId
   * @param {number} planId
   */
  async deletePlan(spaceId, projectId, planId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}`;
      return await this.authenticatedFetch(url, { method: 'DELETE' });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // 지원·멤버 관리 API
  // ========================================

  /**
   * 지원 목록 조회 (founder 전용)
   * @param {number} spaceId
   * @param {string} [status] - 'pending' | 'accepted' | 'rejected'
   */
  async getApplications(spaceId, status) {
    try {
      const url = status
        ? `${this.baseUrl}/api/gig/spaces/${spaceId}/applications?status=${encodeURIComponent(status)}`
        : `${this.baseUrl}/api/gig/spaces/${spaceId}/applications`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 지원하기
   * @param {number} spaceId
   * @param {Object} data - { message }
   */
  async applyToSpace(spaceId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/applications`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * 내 지원 1건 + 팀원 여부 조회
   * @param {number} spaceId
   * @returns {Promise<{ success: boolean, application?: object|null, isMember: boolean }>}
   */
  async getMyApplication(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/my-application`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * 지원자 + 팀원 + 프로젝트 목록 조회
   * @param {number} spaceId
   */
  async getMembers(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/members`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * 지원자 승인/거절
   * @param {number} spaceId
   * @param {number} applicationId
   * @param {string} action - 'accept' | 'reject'
   */
  async handleApplication(spaceId, applicationId, action) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/applications/${applicationId}`;
      return await this.authenticatedFetch(url, {
        method: 'PUT',
        body: JSON.stringify({ action })
      });
    } catch (error) {

      throw error;
    }
  }

  /**
   * 팀원 권한/상태/프로젝트 접근 수정
   * @param {number} spaceId
   * @param {number} participantId
   * @param {Object} data - { role?, status?, projectIds? }
   */
  async updateMember(spaceId, participantId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/members/${participantId}`;
      return await this.authenticatedFetch(url, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // PLAN 담당자 API
  // ========================================

  async updatePlanAssignees(spaceId, projectId, planId, participantIds) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/assignees`;
      return await this.authenticatedFetch(url, {
        method: 'PATCH',
        body: JSON.stringify({ participant_ids: participantIds }),
      });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // PLAN 댓글 API
  // ========================================

  async getPlanComments(spaceId, projectId, planId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/comments`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  async createPlanComment(spaceId, projectId, planId, content) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/comments`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify({ content }),
      });
    } catch (error) {

      throw error;
    }
  }

  async deletePlanComment(spaceId, projectId, planId, commentId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/comments/${commentId}`;
      return await this.authenticatedFetch(url, { method: 'DELETE' });
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 명의 커뮤니티 게시물 목록 조회
   * GET /api/posts?status=published&space_id=:spaceId
   * @param {number} spaceId
   * @param {number} page
   */
  /**
   * SPACE 명의 커뮤니티 게시글 발행
   * POST /api/save_post  (space_id 포함)
   * @param {number} spaceId
   * @param {{ title: string, content: string, abstract: string, category: string }} data
   */
  async createSpacePost(spaceId, { title, content, abstract, category }) {
    try {
      const url = `${this.baseUrl}/api/save_post`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify({
          title,
          content: content || '',
          abstract: abstract || title,
          category: category || '1',
          tags: '',
          postType: 'published',
          space_id: spaceId,
        }),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * SPACE 초대 목록 (관리자)
   * @param {number} spaceId
   * @param {string} [status] - pending | accepted | rejected | cancelled
   */
  async getSpaceInvitations(spaceId, status) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/invitations${qs}`;
    return await this.authenticatedFetch(url);
  }

  /**
   * 팀원 초대 (닉네임 또는 이메일)
   * @param {number} spaceId
   * @param {{ identifier: string }} data
   */
  async inviteMember(spaceId, data) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/invitations`;
      return await this.authenticatedFetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * 내가 받은 대기 중 GIG 초대 목록
   */
  async getMyInvitations() {
    const url = `${this.baseUrl}/api/gig/invitations`;
    return await this.authenticatedFetch(url);
  }

  /**
   * GIG 초대 수락/거절/취소
   * @param {number} spaceId
   * @param {number} invitationId
   * @param {'accept'|'reject'|'cancel'} action
   */
  async respondToInvitation(spaceId, invitationId, action) {
    const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/invitations/${invitationId}`;
    return await this.authenticatedFetch(url, {
      method: 'PUT',
      body: JSON.stringify({ action }),
    });
  }

  // ========================================
  // CHAT 연결 API
  // ========================================

  /**
   * Space에 연결된 chat group ID 조회
   * @param {number} spaceId
   * @returns {Promise<{ success: boolean, chatGroupId: number|null }>}
   */
  async getSpaceChat(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/chat`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Project에 연결된 chat group ID 조회
   * @param {number} spaceId
   * @param {number} projectId
   * @returns {Promise<{ success: boolean, chatGroupId: number|null }>}
   */
  async getProjectChat(spaceId, projectId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/chat`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Space에 속한 모든 채팅 그룹 목록 조회 (GIG 관리용)
   * @param {number} spaceId
   * @returns {Promise<{ success: boolean, chats: Array<{ id, name, isPasswordProtected, memberCount, gigProjectId, gigProjectTitle }> }>}
   */
  async getSpaceChats(spaceId) {
    try {
      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/chats`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // 마이그레이션 API
  // ========================================

  /**
   * 마이그레이션 대상 그룹 수 조회
   * @returns {Promise<{ success: boolean, pendingGroups: number }>}
   */
  async getMigrationStatus() {
    try {
      const url = `${this.baseUrl}/api/gig/admin/migrate-groups`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * gig_posts → gig_spaces 마이그레이션 상태 조회
   * @returns {Promise<{ success: boolean, hasGigPosts: boolean, pendingGigPosts: number }>}
   */
  async getGigPostsMigrationStatus() {
    try {
      const url = `${this.baseUrl}/api/gig/admin/migrate-gig-posts`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * gig_posts → gig_spaces 마이그레이션 실행
   * @returns {Promise<{ success: boolean, migratedCount: number }>}
   */
  async runGigPostsMigration() {
    try {
      const url = `${this.baseUrl}/api/gig/admin/migrate-gig-posts`;
      return await this.authenticatedFetch(url, { method: 'POST' });
    } catch (error) {

      throw error;
    }
  }

  /**
   * 기존 그룹 챗을 GIG Space로 마이그레이션 실행
   * @returns {Promise<{ success: boolean, migratedCount: number, totalPending: number, errors?: string[] }>}
   */
  async runMigration() {
    try {
      const url = `${this.baseUrl}/api/gig/admin/migrate-groups`;
      return await this.authenticatedFetch(url, { method: 'POST' });
    } catch (error) {

      throw error;
    }
  }

  // ========================================
  // 사용자 활동 API
  // ========================================

  /**
   * 사용자의 월별 공개 게시글 활동 조회 (위젯 달력용)
   * 사용자 PC 시간대 기준으로 날짜가 집계됨.
   * @param {number|null} userId - 조회할 사용자 ID (null이면 로그인한 사용자)
   * @param {number} year - 조회할 년도
   * @param {number} month - 조회할 월 (1-12)
   * @param {number} [timezoneOffsetMinutes] - PC 시간대 오프셋(분). 생략 시 브라우저 값 사용 (UTC+9 → 540)
   * @returns {Promise<Object>} { success: true, year, month, userId, activities: { 'YYYY-MM-DD': { count: number } } }
   */
  async getUserActivity(userId, year, month, timezoneOffsetMinutes) {
    try {
      const params = new URLSearchParams();
      if (userId) {
        params.append('userId', userId);
      }
      params.append('year', year);
      params.append('month', month);
      const offset = timezoneOffsetMinutes !== undefined
        ? timezoneOffsetMinutes
        : -new Date().getTimezoneOffset();
      params.append('timezoneOffset', offset);

      const url = `${this.baseUrl}/api/user/activity/calendar?${params}`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  /**
   * SPACE 대시보드 통계 — 활성 캘린더 + 발행 포스트 추이
   * @param {number} spaceId
   * @param {number} year
   * @param {number} month - 1~12
   * @param {number} [timezoneOffsetMinutes] - 생략 시 브라우저 값 사용
   * @returns {Promise<{ success: boolean, activity: Object, posts: { total: number, thisWeek: number, weekly: Array } }>}
   */
  async getDashboard(spaceId, year, month, timezoneOffsetMinutes) {
    try {
      const params = new URLSearchParams();
      params.append('year', year);
      params.append('month', month);
      const offset = timezoneOffsetMinutes !== undefined
        ? timezoneOffsetMinutes
        : -new Date().getTimezoneOffset();
      params.append('timezoneOffset', offset);

      const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/dashboard?${params}`;
      return await this.authenticatedFetch(url);
    } catch (error) {

      throw error;
    }
  }

  // ── 메모 보드 ──────────────────────────────────────────────

  async getMemos(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/memos`);
  }

  async createMemo(spaceId, { content, pos_x, pos_y, color }) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/memos`, {
      method: 'POST',
      body: JSON.stringify({ content, pos_x, pos_y, color }),
    });
  }

  async updateMemo(spaceId, memoId, data) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMemo(spaceId, memoId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}`, {
      method: 'DELETE',
    });
  }

  async toggleMemoReaction(spaceId, memoId, emoji) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}/reactions`,
      { method: 'POST', body: JSON.stringify({ emoji }) }
    );
  }

  async getMemoComments(spaceId, memoId) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}/comments`
    );
  }

  async createMemoComment(spaceId, memoId, content) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}/comments`,
      { method: 'POST', body: JSON.stringify({ content }) }
    );
  }

  async deleteMemoComment(spaceId, memoId, commentId) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/memos/${memoId}/comments/${commentId}`,
      { method: 'DELETE' }
    );
  }

  // ========================================
  // 플랜 담당자 배정 API
  // ========================================

  async getPlanAssignees(spaceId, projectId, planId) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/assign`
    );
  }

  async assignUserToPlan(spaceId, projectId, planId, userId) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/assign`,
      { method: 'POST', body: JSON.stringify({ userId }) }
    );
  }

  async unassignUserFromPlan(spaceId, projectId, planId, userId) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/projects/${projectId}/plans/${planId}/assign`,
      { method: 'DELETE', body: JSON.stringify({ userId }) }
    );
  }

  // ========================================
  // 내 할일 API (담당 플랜 전체 조회)
  // ========================================

  async getMyTasks(status = 'active') {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/user-stats/gig/assigned-plans?status=${status}`
    );
  }

  // ========================================
  // 소개 페이지 (SPACE 발행)
  // ========================================

  /**
   * 소개 페이지 조회 — 비로그인/비멤버도 호출 가능(published일 때만 blocks 반환)
   * @param {number} spaceId
   * @param {boolean} [draft] - true면 관리자 전용 draft 전체 조회
   */
  async getSpacePage(spaceId, draft = false) {
    const url = `${this.baseUrl}/api/gig/spaces/${spaceId}/page${draft ? '?draft=1' : ''}`;
    return this.optionalAuthFetch(url);
  }

  async saveSpacePage(spaceId, { blocks, theme = 'default' }) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/page`, {
      method: 'PUT',
      body: JSON.stringify({ blocks, theme }),
    });
  }

  async publishSpacePage(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/page/publish`, { method: 'POST' });
  }

  async unpublishSpacePage(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/page/unpublish`, { method: 'POST' });
  }

  // ========================================
  // 예산 관리
  // ========================================

  async getBudget(spaceId, { from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget${qs}`);
  }

  async getBudgetDashboard(spaceId, { from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/dashboard${qs}`);
  }

  async getBudgetEquity(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/equity`);
  }

  async createBudgetEquity(spaceId, data) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/equity`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBudgetSummary(spaceId, year, month) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/summary?year=${year}&month=${month}`);
  }

  async getBudgetEntries(spaceId, { from, to, category, status, kind, page = 1, pageSize = 50 } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (category != null) params.set('category', category);
    if (status) params.set('status', status);
    if (kind) params.set('kind', kind);
    params.set('page', page);
    params.set('pageSize', pageSize);
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries?${params.toString()}`);
  }

  async getBudgetEntryMetrics(spaceId, { from, to, kind } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (kind) params.set('kind', kind);
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/metrics?${params.toString()}`);
  }

  async createBudgetEntry(spaceId, data) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBudgetEntry(spaceId, entryId, data) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /** 예산 증빙 파일 업로드 — /api/upload_file */
  async uploadBudgetFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${this.baseUrl}/api/upload_file`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) {
      throw new Error(result.message || '파일 업로드에 실패했습니다.');
    }
    return {
      url: result.fileRef || result.publicUrl || result.url,
      name: result.originalName || file.name,
      fileKey: result.fileKey,
      size: result.size ?? file.size,
      mimeType: result.mimeType || file.type,
    };
  }

  async deleteBudgetEntry(spaceId, entryId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}`, {
      method: 'DELETE',
    });
  }

  async approveBudgetEntry(spaceId, entryId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}/approve`, { method: 'POST' });
  }

  async rejectBudgetEntry(spaceId, entryId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}/reject`, { method: 'POST' });
  }

  async confirmBudgetEntry(spaceId, entryId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}/confirm`, { method: 'POST' });
  }

  async voidBudgetEntry(spaceId, entryId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/entries/${entryId}/void`, { method: 'POST' });
  }

  async saveBudgetCategories(spaceId, categories) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/categories`, {
      method: 'PUT',
      body: JSON.stringify({ categories }),
    });
  }

  /** 기존 카테고리를 전부 지우고 현재 org_type 기본값으로 재시딩 (파괴적 동작, admin 전용) */
  async resetBudgetCategories(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/categories/reset`, {
      method: 'POST',
    });
  }

  async getBudgetDues(spaceId) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/dues`);
  }

  async createBudgetDue(spaceId, data) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/dues`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async setBudgetDuePayment(spaceId, dueId, participantId, paid) {
    return this.authenticatedFetch(
      `${this.baseUrl}/api/gig/spaces/${spaceId}/budget/dues/${dueId}/payments/${participantId}`,
      { method: 'PATCH', body: JSON.stringify({ paid }) },
    );
  }

  async getBudgetPlans(spaceId, { from, to } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/plans${qs}`);
  }

  async saveBudgetPlans(spaceId, plans) {
    return this.authenticatedFetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/plans`, {
      method: 'PUT',
      body: JSON.stringify({ plans }),
    });
  }

  /** CSV Blob 반환 — 호출 측에서 URL.createObjectURL로 다운로드 트리거 */
  async exportBudgetCsv(spaceId, { from, to, status, category, kind } = {}) {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (status) params.set('status', status);
    if (category != null) params.set('category', category);
    if (kind) params.set('kind', kind);
    const token = this.getAuthToken();
    const response = await fetch(`${this.baseUrl}/api/gig/spaces/${spaceId}/budget/export?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `HTTP ${response.status}`);
    }
    return response.blob();
  }
}

const gigService = new GigService();
export default gigService;
